import type { UseQueryResult } from '@tanstack/react-query';
import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { PARTNER_TEAM_PATH } from '@hooks/requests/utils/paths';

// ── Types ──────────────────────────────────────────────────────────────────────

/** `"invited"` until the member has logged in at least once. */
export type DashboardMemberStatus = 'active' | 'invited';

/**
 * A person with access to the partner dashboard, as returned by
 * `GET /api/v1/partner/team`.
 *
 * The roster is Auth0 organization membership rather than anything stored in
 * an AceUp datastore — ally proxies the read, because the scopes it needs are
 * only ever issued to a confidential client and a Management API token is
 * tenant-wide. Which organization is read comes from the caller's own `org_id`
 * claim, so this hook has no organization parameter to pass.
 *
 * Already camelCase on the wire (the endpoint serialises its DTO `by_alias`),
 * so unlike `usePartnerOrganizationQuery` there is no `keysToCamel` step here.
 */
export interface DashboardMember {
  userId: string;
  /** Null when the Auth0 profile carries no name; fall back to email. */
  name: string | null;
  email: string | null;
  picture: string | null;
  /**
   * Canonical role labels (`PartnerAdmin`, `AceUpViewer`, …). The backend does
   * not collapse these into one display role — `getDisplayRole` in
   * `usePartnerRoles` owns that precedence for both the token and this payload.
   */
  roles: string[];
  /** ISO 8601 — the Auth0 account's creation date ("Member Since"). */
  joinedAt: string | null;
  /**
   * ISO 8601 of the last interactive login. A floor on activity rather than a
   * true last-seen: it only updates when the member logs in again.
   */
  lastActive: string | null;
  status: DashboardMemberStatus;
}

export interface UsePartnerTeamParams {
  /**
   * Defaults to `true`. Pass `false` for viewer roles: the endpoint is
   * admin-only and would answer 403, so there is nothing to ask for.
   */
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * The organization's dashboard roster. **Admin roles only** — both viewer
 * roles are refused by the API, not merely hidden in the UI.
 *
 * Arrives ordered most-recently-active first with never-logged-in members
 * last; `TeamTable` layers the current-user pin and column sorting on top.
 *
 * Story 2 (sc-43661) adds invite/edit/remove mutations that invalidate this
 * query — they should reuse `PARTNER_TEAM_PATH` for the cache key rather than
 * re-stating the path.
 */
export const usePartnerTeamQuery = ({
  enabled = true,
}: UsePartnerTeamParams = {}): UseQueryResult<DashboardMember[], Error> =>
  useBasicQuery<DashboardMember[]>(PARTNER_TEAM_PATH, undefined, { enabled });

export default usePartnerTeamQuery;
